import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { toast } from 'react-toastify'
import { jwtDecode } from 'jwt-decode'
import { deviceDetect } from 'react-device-detect'
import request from 'src/utils/request'

const STORAGE_KEY = 'teacherInfo'

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

const getErrorMessage = (err, fallback = 'Une erreur est survenue') => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data ||
    err?.message ||
    fallback
  )
}

const getStoredTeacher = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    const token = parsed?.token
    if (!token) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    const decoded = jwtDecode(token)
    if (!decoded?.exp || decoded.exp * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return parsed
  } catch (error) {
    console.error('Erreur lecture token localStorage:', error)
    localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

export const loginUser = createAsyncThunk('auth/loginUser', async ({ user }, thunkAPI) => {
  try {
    const deviceInfo = deviceDetect()

    const payload = {
      ...user,
      deviceInfo,
    }

    const { data } = await api.post('/teachers/auth', payload)

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    return data
  } catch (err) {
    const message = getErrorMessage(err, 'Échec de connexion')
    toast.error(message)
    return thunkAPI.rejectWithValue(message)
  }
})

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, thunkAPI) => {
  try {
    localStorage.removeItem(STORAGE_KEY)
    return true
  } catch (error) {
    const message = getErrorMessage(error, 'Déconnexion impossible')
    toast.error(message)
    return thunkAPI.rejectWithValue(message)
  }
})

export const getTeacherByID = createAsyncThunk(
  'auth/getTeacherByID',
  async ({ ID_ENS }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token
      const { data } = await request(token).get('/teachers/teacher', {
        params: { ID_ENS },
      })
      return data
    } catch (err) {
      const message = getErrorMessage(err, "Impossible de récupérer l'enseignant")
      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const changeTeacherPassword = createAsyncThunk(
  'auth/changeTeacherPassword',
  async ({ ID_ENS, password, newPassword, lang }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token

      const { data } = await request(token).put('/teachers/change-password', {
        ID_ENS,
        password,
        newPassword,
      })

      toast.success(
        lang === 'Français'
          ? 'Votre mot de passe a été modifié avec succès'
          : 'Your password updated successfully',
      )

      localStorage.removeItem(STORAGE_KEY)
      return data
    } catch (err) {
      const message =
        err?.response?.status === 400
          ? lang === 'Français'
            ? 'Erreur ! Votre ancien mot de passe est incorrect'
            : 'Error! Your old password is incorrect'
          : lang === 'Français'
          ? "Erreur ! Votre mot de passe n'a pas pu être modifié"
          : 'Error! Your password could not be changed'

      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const checkTeacher = createAsyncThunk(
  'auth/checkTeacher',
  async ({ id, type }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token
      const { data } = await request(token).get('/teachers/auth', {
        params: { id, type },
      })
      return data
    } catch (err) {
      const message = getErrorMessage(err, 'Vérification du compte impossible')
      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const changeLanguage = createAsyncThunk(
  'auth/changeLanguage',
  async ({ id_ens, language, lang }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token

      await request(token).put('/teachers/changeLanguage', {
        id_ens,
        language,
      })

      const { data } = await request(token).get('/teachers/auth', {
        params: { id: id_ens, type: 'id' },
      })

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))

      toast.success(
        lang === 'Français' ? 'Profil modifié avec succès' : 'Profile updated successfully',
      )

      return data
    } catch (err) {
      const message = getErrorMessage(err, 'Modification de langue impossible')
      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const updateTeacher = createAsyncThunk(
  'auth/updateTeacher',
  async ({ formData, lang }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token

      const { data } = await request(token).put('/teachers/teacher', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      })

      toast.success(
        lang === 'Français'
          ? 'Votre profil a été modifié avec succès'
          : 'Your profile updated successfully',
      )

      return data
    } catch (err) {
      const message =
        lang === 'Français'
          ? "Erreur ! Votre profil n'a pas pu être modifié"
          : 'Error! Your profile could not be modified'

      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const getTeacherByEmail = createAsyncThunk(
  'auth/getTeacherByEmail',
  async ({ MAIL_PRO_ENS }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token
      const { data } = await request(token).get('/teachers/profile', {
        params: { MAIL_PRO_ENS },
      })
      return data
    } catch (err) {
      const message = getErrorMessage(err, 'Impossible de récupérer le profil')
      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

export const updateProfileTeacher = createAsyncThunk(
  'auth/updateProfileTeacher',
  async ({ viewProfile, ID_ENS, lang }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.teacher?.token

      await request(token).put('/teachers/viewProfile', viewProfile)

      const { data } = await request(token).get('/teachers/auth', {
        params: { id: ID_ENS, type: 'id' },
      })

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))

      toast.success(
        lang === 'Français' ? 'Profil modifié avec succès' : 'Profile updated successfully',
      )

      return data
    } catch (err) {
      const message = getErrorMessage(err, 'Mise à jour du profil impossible')
      toast.error(message)
      return thunkAPI.rejectWithValue(message)
    }
  },
)

const initialState = {
  teacher: getStoredTeacher(),
  currentTeacherData: [],
  registerMessage: null,
  isLoading: false,
  isSuccess: false,
  error: false,
  successMessage: '',
  errorMessage: '',
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    messageClear(state) {
      state.errorMessage = ''
      state.successMessage = ''
      state.error = false
    },
    login(state, action) {
      state.teacher = action.payload
      state.registerMessage = null
    },
    logout(state) {
      state.teacher = null
      state.currentTeacherData = []
      state.isSuccess = false
      state.error = false
      state.successMessage = ''
      state.errorMessage = ''
      localStorage.removeItem(STORAGE_KEY)
    },
    register(state, action) {
      state.registerMessage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.isSuccess = false
        state.error = false
        state.errorMessage = ''
        state.successMessage = ''
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.error = false
        state.teacher = action.payload
        state.currentTeacherData = action.payload
        state.successMessage = action.payload?.message || ''
        state.errorMessage = ''
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.isSuccess = false
        state.error = true
        state.errorMessage = action.payload || 'Échec de connexion'
      })

      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.teacher = null
        state.currentTeacherData = []
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = true
        state.errorMessage = action.payload || 'Unsuccessful logout'
      })

      .addCase(getTeacherByEmail.fulfilled, (state, action) => {
        state.currentTeacherData = action.payload
      })

      .addCase(getTeacherByID.fulfilled, (state, action) => {
        state.currentTeacherData = action.payload
      })

      .addCase(changeTeacherPassword.fulfilled, (state) => {
        state.teacher = null
      })

      .addCase(updateProfileTeacher.pending, (state) => {
        state.isLoading = true
        state.isSuccess = false
      })
      .addCase(updateProfileTeacher.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.teacher = action.payload
        state.error = false
      })
      .addCase(updateProfileTeacher.rejected, (state, action) => {
        state.isLoading = false
        state.isSuccess = false
        state.error = true
        state.errorMessage = action.payload || 'Mise à jour impossible'
      })

      .addCase(changeLanguage.pending, (state) => {
        state.isLoading = true
        state.isSuccess = false
      })
      .addCase(changeLanguage.fulfilled, (state, action) => {
        state.isLoading = false
        state.isSuccess = true
        state.teacher = action.payload
        state.error = false
      })
      .addCase(changeLanguage.rejected, (state, action) => {
        state.isLoading = false
        state.isSuccess = false
        state.error = true
        state.errorMessage = action.payload || 'Modification de langue impossible'
      })
  },
})

export const { messageClear, login, logout, register } = authSlice.actions
export const authReducer = authSlice.reducer
